import { useCallback } from 'react';
import { url } from '../../../api';
import { apiClient } from '../../../api/client';
import { coffeeChainPaths } from '../../coffee_card_web/api/paths';
import { getAuthToken } from '../../../utils/authTokens';

const authHeaders = () => ({ headers: { Authorization: `Bearer ${getAuthToken()}` } });

export function useChainMutations(refetch: () => void) {
    const create = useCallback(async (body: { name: string; slug?: string; description?: string; website?: string; country?: string }) => {
        const res = await apiClient.post(`${url}${coffeeChainPaths.create()}`, body, authHeaders());
        refetch();
        return res.data?.chain;
    }, [refetch]);

    const update = useCallback(async (id: string, body: Partial<{ name: string; slug: string; description: string; website: string; country: string; isActive: boolean }>) => {
        const res = await apiClient.put(`${url}${coffeeChainPaths.byId(id)}`, body, authHeaders());
        refetch();
        return res.data?.chain;
    }, [refetch]);

    const remove = useCallback(async (id: string) => {
        await apiClient.delete(`${url}${coffeeChainPaths.byId(id)}`, authHeaders());
        refetch();
    }, [refetch]);

    const assign = useCallback(async (chainId: string, shopIds: string[]) => {
        const res = await apiClient.post(`${url}${coffeeChainPaths.assign(chainId)}`, { shopIds }, authHeaders());
        refetch();
        return res.data;
    }, [refetch]);

    const unassign = useCallback(async (chainId: string, shopIds: string[]) => {
        const res = await apiClient.post(`${url}${coffeeChainPaths.unassign(chainId)}`, { shopIds }, authHeaders());
        refetch();
        return res.data;
    }, [refetch]);

    const searchShops = useCallback(async (chainId: string, query: string) => {
        const res = await apiClient.post(`${url}${coffeeChainPaths.searchShops(chainId)}`, { query }, authHeaders());
        return res.data?.shops || [];
    }, []);

    const createRule = useCallback(async (chainId: string, body: { field?: string; operator: string; value: string; caseSensitive?: boolean }) => {
        const res = await apiClient.post(`${url}${coffeeChainPaths.rules(chainId)}`, body, authHeaders());
        return res.data?.rule;
    }, []);

    const deleteRule = useCallback(async (chainId: string, ruleId: string) => {
        await apiClient.delete(`${url}${coffeeChainPaths.ruleById(chainId, ruleId)}`, authHeaders());
    }, []);

    const runRules = useCallback(async (unassignedOnly = true) => {
        const res = await apiClient.post(`${url}${coffeeChainPaths.runRules()}`, { unassignedOnly }, authHeaders());
        refetch();
        return res.data;
    }, [refetch]);

    return { create, update, remove, assign, unassign, searchShops, createRule, deleteRule, runRules };
}
